import React from 'react';
import {
    Box, Typography, Chip, Card, CardMedia, CardContent, Button, Stack,
    Divider,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';
import { blue } from '@mui/material/colors';
import { getImageUrl, stripHtml } from '../utils/utils';
import ProjectLinks from './projects/ProjectLinks';

const ProjectCard = ({ project, onViewDetails }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const hasAnyLink = (links) => links && Object.values(links).some((v) => v?.trim() !== "");
    return (
        <Card
            sx={{
                height: '100%',
                borderRadius: '.75rem',
                boxShadow: theme.palette.custom.cardShadow,
                position: 'relative',
                overflow: 'hidden',
                transition: 'all .5s cubic-bezier(.4, 0, .2, 1)',
                '&:before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    bottom: 0,
                    left: 0,
                    opacity: 0,
                    transition: 'opacity .3s cubic-bezier(.4, 0, .2, 1)',
                    background: 'linear-gradient(135deg, hsl(var(--nexus-cyan) / .1), hsl(var(--nexus-lime) / .1))',
                    zIndex: 1,
                    pointerEvents: 'none',
                },
                '&:hover': {
                    transform: 'translateY(-6px)',
                    boxShadow: theme.palette.custom.cardShadowHover,
                    '&:before': {
                        opacity: 1,
                    },
                    '& .hover-overlay': {
                        opacity: 1,
                    },
                    '& .project-title': {
                        color: theme.palette.text.link,
                    }
                },
            }}
        >
            <Box sx={{ position: 'relative', zIndex: 2 }}>
                <CardMedia
                    component="img"
                    image={getImageUrl(project?.image_path)}
                    alt={project?.title}
                    sx={{
                        height: { xs: 180, sm: 200, md: 300 },
                        objectFit: 'contain',
                    }}
                />

                <Box
                    className="hover-overlay"
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        backgroundColor: theme.palette.custom.overlay,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        opacity: 0,
                        transition: 'opacity 0.3s ease',
                    }}
                >
                    <Button
                        variant="contained"
                        endIcon={<ArrowForwardIcon />}
                        onClick={() => onViewDetails(project)}
                        sx={{
                            backgroundColor: theme.palette.common.white,
                            color: theme.palette.text.link,
                            textTransform: 'none',
                            borderRadius: '20px',
                            px: 2,
                            '&:hover': {
                                backgroundColor: theme.palette.grey[100],
                            },
                        }}
                    >
                        <Typography variant="textSm">
                            {t('pages.home.portfolio.view_details')}
                        </Typography>
                    </Button>
                </Box>
            </Box>

            <CardContent sx={{ p: 4, position: 'relative', zIndex: 2 }}>
                <Typography variant="overline" color={blue[500]} fontWeight="500">
                    {project.category?.name?.toUpperCase()}
                </Typography>
                <Typography variant="h6" component="h3" className='project-title' fontWeight="600" sx={{
                    mt: 1,
                    mb: 1,
                    fontFamily: theme.typography.h3.fontFamily,
                    wordBreak: 'break-word'
                }}>
                    {project?.title}
                </Typography>
                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                        mb: 3,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                    }}
                >
                    {stripHtml(project?.description)}
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {project?.technology.map((tag, index) => (
                        <Chip
                            key={`${tag}-${index}`}
                            label={tag}
                            size="small"
                            sx={{
                                backgroundColor: theme.palette.custom.tagBg,
                                color: theme.palette.custom.tagText,
                                fontWeight: 500
                            }}
                        />
                    ))}
                </Stack>

                {
                    hasAnyLink(project?.links) && (
                        <>
                            <Divider sx={{ my: 2 }} />
                            <ProjectLinks
                                links={project.links}
                                chipSx={{
                                    backgroundColor: "transparent",
                                    color: theme.palette.primary.main,
                                    '&:hover': {
                                        backgroundColor: 'transparent',
                                    },
                                    '& .MuiChip-icon': {
                                        color: theme.palette.primary.main,
                                    },
                                }}
                                stackSx={{
                                    spacing: 0,
                                    gap: 0,
                                    justifyContent: 'flex-start',
                                }}
                            />
                        </>
                    )
                }


            </CardContent>
        </Card>
    );
};

export default ProjectCard;
